<template>
    <div style="height: inherit">
        <section class="team-chat-app-window position-relative mr-2">
            <div v-if="!isChat" class="no-chats mt-2 d-flex justify-content-center align-items-center flex-column">
                <div class="chats-preview-container mx-auto">
                    <img src="~@/assets/images/team-chat-zero-state.svg" />
                </div>

                <h4>{{ chatsCount ? $t("team-chats.no-chat-selected") : $t("team-chats.no-chats") }}</h4>

                <p class="text-secondary">
                    {{
                        chatsCount
                            ? $t("team-chats.no-chat-selected-description")
                            : $t("team-chats.no-chats-description")
                    }}
                </p>

                <b-button class="d-lg-none mt-1" @click="mqShallShowLeftSidebar = true">
                    <font-awesome-icon icon="comment-lines" size="xl" class="mr-1" />
                    {{ $t("entities.conversation.start") }}
                </b-button>
            </div>

            <active-team-chat-header
                v-if="isChat"
                :active-chat="activeChat"
                :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
                @set-edited-chat="setEditedChat"
            />

            <div class="user-chats-wrapper">
                <div
                    v-if="isChat"
                    ref="refActiveTeamChatLogPS"
                    :class="
                        !activeChat.isArchived
                            ? ['user-chats', 'scroll-area', 'scroll-container']
                            : ['user-chats', 'no-compose', 'scroll-area', 'scroll-container']
                    "
                    infinite-wrapper
                >
                    <active-team-chat-log :active-chat="activeChat" @scroll-to-bottom="scrollToBottomInActiveChatLog" />
                </div>
            </div>
            <active-team-chat-compose v-if="isChat && !activeChat.isArchived" :active-chat="activeChat" />
        </section>

        <portal to="content-renderer-sidebar-left">
            <team-chat-left-sidebar
                :create-form="createForm"
                :edit-chat="editChat"
                :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
                @set-edited-chat="setEditedChat"
                @toggle-create-team-chat="toggleCreateTeamChat"
                @team-chat-clicked="teamChatClicked"
            />
        </portal>
    </div>
</template>

<script>
import useTeamChat from "./useTeamChat";
import { BButton, BImg, BAvatar } from "bootstrap-vue";
import TeamChatLeftSidebar from "./team-chat-left-sidebar/teamChatLeftSidebar.vue";
import i18n from "@/libs/i18n";
import useActiveTeamChat from "./useActiveTeamChat";
import { useToast } from "vue-toastification/composition";
import { useRouter } from "@core/utils/utils";
import ActiveTeamChatLog from "./active-chat/activeTeamChatLog.vue";
import ActiveTeamChatHeader from "./active-chat/activeTeamChatHeader.vue";
import { resolveInitials } from "@/utils/users";
import { watch, computed, ref, onMounted } from "@vue/composition-api";
import ActiveTeamChatCompose from "./active-chat/activeTeamChatCompose.vue";
import { useResponsiveAppLeftSidebarVisibility } from "@core/comp-functions/ui/app";
import useTour from "@/composables/useTour";
import router from "@/router";
import { useBreakpoints } from "@vueuse/core";
// @ts-ignore
import { $themeBreakpoints } from "@themeConfig";

export default {
    components: {
        BButton,
        BImg,
        BAvatar,
        TeamChatLeftSidebar,
        ActiveTeamChatLog,
        ActiveTeamChatHeader,
        ActiveTeamChatCompose,
    },
    setup() {
        const { createForm, editChat, toggleCreateTeamChat, setEditedChat, chats, updateEntryStatus } = useTeamChat();
        const { loadActiveChat, activeChat, refActiveTeamChatLogPS, scrollToBottomInActiveChatLog } =
            useActiveTeamChat();
        const toast = useToast();
        const { route, router } = useRouter();
        const isChat = ref(Boolean(router.currentRoute.params.chatId));

        const routeParams = computed(() => route.value.params);

        const chatsCount = computed(() => chats.value.length);

        const loadChat = () => {
            if (router.currentRoute.params.chatId) {
                loadActiveChat()
                    .then(() => {
                        isChat.value = true;
                    })
                    .catch(() => {
                        toast.error(i18n.t("entities.conversation.could-not-open"));

                        return router.replace({
                            name: "team-chat",
                        });
                    });
            } else {
                activeChat.value = null;
                isChat.value = false;
            }
        };

        watch(routeParams, () => {
            loadChat();
        });

        loadChat();
        updateEntryStatus("delivered");

        // UI (Responsive left sidebar)
        const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility();

        onMounted(() => {
            // On small screens, if no chat id is set, we want to show the left sidebar instead of the zero state
            if (!router.currentRoute.params.chatId && useBreakpoints($themeBreakpoints).isSmaller("lg"))
                mqShallShowLeftSidebar.value = true;
        });

        const teamChatClicked = () => {
            mqShallShowLeftSidebar.value = false;
        };

        return {
            createForm,
            editChat,
            activeChat,
            isChat,
            toggleCreateTeamChat,
            setEditedChat,
            resolveInitials,
            refActiveTeamChatLogPS,
            chatsCount,
            scrollToBottomInActiveChatLog,
            mqShallShowLeftSidebar,
            teamChatClicked,
        };
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-context.scss";
@import "~@/assets/scss/modules/team-chat/teamChat.scss";
</style>
